import React, { useMemo } from 'react';
import { Lead, Contact, CrmUser, FunnelStage } from '@types';
import { useLeads } from '../../../contexts/leads/LeadsContext';
import KanbanColumn from './KanbanColumn';
import { KANBAN_OTHER_FUNNEL_STAGE_ID } from '../constants';
import { LossReasonModal } from './modals/LossReasonModal';
import { WinDealModal } from './modals/WinDealModal';
import { useKanbanDragDrop } from '../hooks/useKanbanDragDrop';
import { useDealModals } from '../hooks/useDealModals';
import { useTags } from '../../settings/tags/hooks/useTags';
import { cn } from '@/lib/utils';
import { MOBILE_CONTENT_BOTTOM_PAD_CLASS } from '@/components/layout/layoutNavTokens';

interface KanbanBoardProps {
    leadsByStage: { [key: string]: Lead[] };
    onCardClick: (lead: Lead) => void;
    onCreateLead?: (stageId: string) => void;
    onChatClick?: (lead: Lead) => void;
    contacts: Contact[];
    users: CrmUser[];
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({
    leadsByStage,
    onCardClick,
    onCreateLead,
    onChatClick,
    contacts,
}) => {
    const { funnelStages, updateLeadStage } = useLeads();
    const { tags } = useTags();

    const otherFunnelStage = useMemo<FunnelStage>(
        () => ({
            id: KANBAN_OTHER_FUNNEL_STAGE_ID,
            name: 'Outro funil',
            conversionEvent: 'none',
        }),
        []
    );

    const otherFunnelLeads = leadsByStage[KANBAN_OTHER_FUNNEL_STAGE_ID] ?? [];
    const {
        draggedColumnId,
        handleColumnDragStart,
        handleColumnDragOver,
        handleColumnDrop,
        handleDragEnd,
    } = useKanbanDragDrop();
    const {
        lossReasonModal,
        winDealModal,
        closeLossModal,
        closeWinModal,
        handleConfirmWin,
        handleConfirmLoss,
    } = useDealModals(leadsByStage);

    const renderColumn = (stage: FunnelStage, index: number, options?: { dropDisabled?: boolean; draggable?: boolean }) => {
        const dropDisabled = options?.dropDisabled ?? false;
        const canDragColumn = options?.draggable ?? !dropDisabled;

        return (
            <div
                key={stage.id}
                draggable={canDragColumn}
                onDragStart={
                    canDragColumn
                        ? (e) => handleColumnDragStart(e, stage.id)
                        : undefined
                }
                onDragEnd={canDragColumn ? handleDragEnd : undefined}
                onDragOver={canDragColumn ? handleColumnDragOver : undefined}
                onDrop={
                    canDragColumn
                        ? (e) => handleColumnDrop(e, stage.id)
                        : undefined
                }
                className={cn(
                    'min-h-0 snap-start snap-always px-s4',
                    'md:snap-align-none md:px-0',
                    'transition-opacity duration-200',
                    draggedColumnId === stage.id && 'opacity-50'
                )}
            >
                <KanbanColumn
                    stage={stage}
                    index={index}
                    leads={leadsByStage[stage.id] || []}
                    onCardClick={onCardClick}
                    onDrop={updateLeadStage}
                    onCreateLead={dropDisabled ? undefined : onCreateLead}
                    onChatClick={onChatClick}
                    contacts={contacts}
                    tags={tags}
                    dropDisabled={dropDisabled}
                />
            </div>
        );
    };

    return (
        <>
            <div
                role="region"
                aria-roledescription="carousel"
                aria-label="Etapas do funil"
                className={cn(
                    'h-full min-h-0 w-full flex-1',
                    'overflow-x-auto overscroll-x-contain scroll-smooth touch-pan-x',
                    'snap-x snap-mandatory',
                    MOBILE_CONTENT_BOTTOM_PAD_CLASS,
                    'md:snap-none md:scroll-auto md:overscroll-auto md:pb-4'
                )}
            >
                {/*
                  Mobile: grid auto-cols 100% → 1 coluna por viewport + snap.
                  Desktop (md+): flex com colunas fixas 280px (layout original).
                */}
                <div
                    className={cn(
                        'grid h-full min-h-0 grid-flow-col auto-cols-[100%] items-stretch pt-s3',
                        'md:flex md:h-auto md:min-w-max md:items-start md:gap-s4 md:px-s6 md:pt-s6'
                    )}
                >
                    {funnelStages.map((stage, index) => renderColumn(stage, index))}

                    {otherFunnelLeads.length > 0
                        ? renderColumn(otherFunnelStage, funnelStages.length, {
                              dropDisabled: true,
                              draggable: false,
                          })
                        : null}
                </div>
            </div>

            <LossReasonModal
                isOpen={lossReasonModal.isOpen}
                leadName={lossReasonModal.leadName}
                onClose={closeLossModal}
                onConfirm={handleConfirmLoss}
            />

            <WinDealModal
                isOpen={winDealModal.isOpen}
                leadName={winDealModal.leadName}
                currentValue={winDealModal.currentValue}
                onClose={closeWinModal}
                onConfirm={handleConfirmWin}
            />
        </>
    );
};

export default KanbanBoard;
